import { OpsTokens } from "./tokens";

export interface ComponentDef {
  styleOverrides?: Record<string, any>;
  defaultProps?: Record<string, any>;
}
export type ComponentSpec = Record<string, ComponentDef>;

export const V5_ONLY = new Set<string>(["MuiDataGrid"]);

export const buildComponents = (t: OpsTokens): ComponentSpec => ({
  MuiAvatar: {
    styleOverrides: {
      img: { objectFit: "contain" },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: "0.25rem",
        width: "1.5rem",
        height: "1.5rem",
        "& .MuiSvgIcon-root": {
          width: "1.5rem",
          height: "1.5rem",
          zIndex: 1,
        },
        "& .PrivateSwitchBase-input[checked]": {
          width: "auto",
          height: "auto",
          top: "auto",
          left: "auto",
          opacity: "1",
          "&::before": {
            content: "''",
            position: "absolute",
            background: "#fff !important",
            height: "100%",
            width: "100%",
          },
        },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: {
        backgroundColor: `${t.surfaceCard} !important`,
        color: t.textSecondary,
        borderTop: `1px solid ${t.borderSubtle}`,
      },
      toolbar: {
        justifyContent: "flex-end",
        marginRight: "1rem",
      },
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        border: `1px solid ${t.borderDefault}`,
        borderRadius: t.radiusCard,
        backgroundImage: "none",
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: t.radiusDialog },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: t.radiusControl,
        border: `1px solid ${t.borderDefault}`,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paperAnchorRight: { width: t.drawerPanelWidth, maxWidth: "100vw" },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { minHeight: t.controlMd },
    },
  },

  MuiSwitch: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: { width: 32, height: 18, padding: 0, display: "flex", overflow: "visible" },
      switchBase: {
        padding: 2,
        color: "#fff",
        "&:hover, &.Mui-checked:hover": { backgroundColor: "transparent" },
        "&.Mui-checked": {
          transform: "translateX(14px)",
          color: "#fff",
          "& + .MuiSwitch-track": { backgroundColor: t.actionPrimary, opacity: 1 },
        },
        "&.Mui-focusVisible + .MuiSwitch-track": { boxShadow: `0 0 0 3px ${t.focusRing}` },
        "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
        "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
      },
      sizeSmall: {
        width: 32,
        height: 18,
        "& .MuiSwitch-thumb": { width: 14, height: 14 },
        "& .MuiSwitch-switchBase": { padding: 2 },
        "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(14px)" },
      },
      thumb: {
        width: 14,
        height: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,.10)",
      },
      track: {
        borderRadius: 9,
        backgroundColor: t.borderStrong,
        opacity: 1,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        height: 20,
        borderRadius: t.radiusChip,
        fontSize: 11,
        fontWeight: 600,
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: "outlined" as const,
      InputLabelProps: { shrink: true },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: "relative",
        transform: "none",
        maxWidth: "100%",
        fontSize: 11,
        lineHeight: "16px",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        fontWeight: 600,
        color: t.textTertiary,
        marginBottom: 4,
        "&.Mui-focused": { color: t.textSecondary },
      },
      formControl: {
        position: "relative",
        transform: "none",
        left: "auto",
        top: "auto",
        "&.MuiInputLabel-shrink": { position: "relative", transform: "none" },
      },
      shrink: { transform: "none" },
      outlined: {
        position: "relative",
        transform: "none",
        "&.MuiInputLabel-shrink": { position: "relative", transform: "none" },
      },
      filled: {
        position: "relative",
        transform: "none",
        "&.MuiInputLabel-shrink": { position: "relative", transform: "none" },
      },
      standard: {
        position: "relative",
        transform: "none",
        "&.MuiInputLabel-shrink": { position: "relative", transform: "none" },
      },
      animated: { transition: "none" },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minHeight: 34,
        "&.MuiInputBase-multiline": { padding: "8px 10px" },
        borderRadius: t.radiusControl,
        backgroundColor: t.surfaceCard,
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.borderStrong },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: t.actionPrimary,
          borderWidth: 1,
        },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${t.focusRing}` },
      },
      notchedOutline: {
        top: 0,
        borderColor: t.borderDefault,
        "& legend": { display: "none" },
      },
      input: {
        paddingTop: 0,
        paddingRight: 10,
        paddingBottom: 0,
        paddingLeft: 10,
        height: 34,
        boxSizing: "border-box",
        fontSize: 12.5,
      },
      inputMultiline: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        height: "auto",
        boxSizing: "border-box",
      },
    },
  },

  MuiFilledInput: {
    styleOverrides: {
      root: {
        minHeight: 34,
        "&.MuiInputBase-multiline": { padding: "8px 10px" },
        borderRadius: t.radiusControl,
        backgroundColor: t.surfaceCard,
        border: `1px solid ${t.borderDefault}`,
        "&:hover": { backgroundColor: t.surfaceCard, borderColor: t.borderStrong },
        "&.Mui-focused": {
          backgroundColor: t.surfaceCard,
          borderColor: t.actionPrimary,
          boxShadow: `0 0 0 3px ${t.focusRing}`,
        },
        "&::before, &::after": { display: "none" },
      },
      input: {
        paddingTop: 0,
        paddingRight: 10,
        paddingBottom: 0,
        paddingLeft: 10,
        height: 34,
        boxSizing: "border-box",
        fontSize: 12.5,
      },
      inputMultiline: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        height: "auto",
        boxSizing: "border-box",
      },
    },
  },

  MuiInput: {
    styleOverrides: {
      root: {
        minHeight: 34,
        "&.MuiInputBase-multiline": { padding: "8px 10px" },
        borderRadius: t.radiusControl,
        backgroundColor: t.surfaceCard,
        border: `1px solid ${t.borderDefault}`,
        "&:hover": { borderColor: t.borderStrong },
        "&.Mui-focused": {
          borderColor: t.actionPrimary,
          boxShadow: `0 0 0 3px ${t.focusRing}`,
        },
        "&::before, &::after": { display: "none" },
      },
      input: {
        paddingTop: 0,
        paddingRight: 10,
        paddingBottom: 0,
        paddingLeft: 10,
        height: 34,
        boxSizing: "border-box",
        fontSize: 12.5,
      },
      inputMultiline: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        height: "auto",
        boxSizing: "border-box",
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        minHeight: 34,
        paddingTop: 0,
        paddingBottom: 0,
        '&[class*="MuiOutlinedInput-root"]': { paddingTop: 0, paddingBottom: 0, paddingLeft: 10 },
      },
      input: { padding: "0 !important", height: 32, fontSize: 12.5 },
    },
  },

  MuiInputAdornment: {
    styleOverrides: {
      positionEnd: { marginLeft: 0, marginRight: 6 },
      positionStart: { marginLeft: 6, marginRight: 0 },
      root: { "& .MuiIconButton-root": { padding: 2 }, "& .MuiSvgIcon-root": { fontSize: 18 } },
    },
  },

  MuiSelect: {
    styleOverrides: {
      select: {
        display: "flex",
        alignItems: "center",
        minHeight: 34,
        padding: "0 10px !important",
        boxSizing: "border-box",
      },
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      anchorOriginBottomCenter: {
        left: "auto",
        right: 16,
        transform: "none",
        bottom: 44,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: t.radiusCard,
        fontSize: t.textBase,
        alignItems: "center",
        boxShadow: "0 12px 32px rgba(14,19,26,.14), 0 2px 8px rgba(14,19,26,.06)",
      },
      filledSuccess: {
        color: t.textInverse,
        backgroundColor: t.surfaceInverse,
        boxShadow: `inset 3px 0 0 ${t.statusSuccessFg}`,
      },
      filledError: {
        color: t.textInverse,
        backgroundColor: t.surfaceInverse,
        boxShadow: `inset 3px 0 0 ${t.statusDangerFg}`,
      },
      filledWarning: {
        color: t.textInverse,
        backgroundColor: t.surfaceInverse,
        boxShadow: `inset 3px 0 0 ${t.statusWarningFg}`,
      },
      filledInfo: {
        color: t.textInverse,
        backgroundColor: t.surfaceInverse,
        boxShadow: `inset 3px 0 0 ${t.statusInfoFg}`,
      },
      icon: { color: "inherit", opacity: 0.9 },
      message: { padding: "6px 0" },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        borderRadius: t.radiusCard,
        border: `1px solid ${t.borderDefault}`,
      },
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: { maxHeight: 34 },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        height: t.rowHeight,
        fontSize: t.textBase,
        borderColor: t.borderSubtle,
        color: t.textSecondary,
        padding: 0,
        textAlign: "center",
        lineHeight: "20px",
        "& a": {
          color: t.textLink,
        },
        "&:first-of-type": {
          textAlign: "left",
          paddingLeft: "2.75rem",
        },
        "&:last-of-type": {
          textAlign: "right",
          paddingRight: "1.5rem",
        },
        "& .MuiListItemText-primary": {
          fontSize: 12,
          textAlign: "center",
        },
      },
      sizeSmall: {
        padding: 0,
        height: t.rowHeight,
        "&:first-of-type": { paddingLeft: "2.75rem" },
      },
      head: {
        height: 34,
        backgroundColor: t.tableHeaderBg,
        whiteSpace: "nowrap",
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: ".06em",
        color: t.textTertiary,
        borderBottom: `1px solid ${t.borderDefault}`,
        "&:not(:first-of-type) span": {
          justifyContent: "center",
        },
      },
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: `1px solid ${t.borderDefault}`,
        borderRadius: t.radiusCard,
      },
    },
  },
});
